import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from 'lib/utils';
import { HIGHLIGHT_COLORS, type HighlightColor } from './annotate';

const SWATCH: Record<HighlightColor, string> = {
  yellow: 'bg-[#FDE68A]',
  green: 'bg-[#BBF7D0]',
  pink: 'bg-[#FBCFE8]',
  blue: 'bg-[#BFDBFE]',
  // White disc with a red bar (drawn inside the button): underline without colour.
  underline: 'bg-white',
};

interface Props {
  color: HighlightColor;
  note: string;
  /** Desktop placement inside the passage pane; phones use a bottom toolbar instead. */
  position: { top: number; left: number } | null;
  onColor: (color: HighlightColor) => void;
  onNote: (note: string) => void;
  onDelete: () => void;
  onClose: () => void;
}

/** Colour, note and delete for one highlight: a popover on wide screens, a toolbar on phones. */
export function HighlightPanel({ color, note, position, onColor, onNote, onDelete, onClose }: Props) {
  const { t } = useTranslation();
  const [noteOpen, setNoteOpen] = useState(Boolean(note));
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const swatches = (large: boolean) =>
    HIGHLIGHT_COLORS.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onColor(c)}
        aria-pressed={color === c}
        aria-label={t(`exam.sat.hl_${c}`)}
        title={t(`exam.sat.hl_${c}_help`)}
        className={cn(
          'shrink-0 rounded-full border-2',
          SWATCH[c],
          large ? 'h-8 w-8' : 'h-[22px] w-[22px]',
          color === c ? 'border-white' : 'border-slate-600',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D1FE17]',
          c === 'underline' && 'flex items-center justify-center',
        )}
      >
        {c === 'underline' && <span aria-hidden className="mt-1 block h-0.5 w-3/5 bg-[#DC2626]" />}
      </button>
    ));

  const noteField = (dark: boolean) => (
    <label className={cn('flex flex-col gap-1.5 text-xs', dark ? 'text-slate-300' : 'text-slate-600')}>
      {t('exam.sat.hl_note')}
      <textarea
        value={note}
        onChange={(e) => onNote(e.target.value)}
        placeholder={t('exam.sat.hl_note_placeholder')}
        maxLength={500}
        rows={2}
        autoFocus={!note}
        className={cn(
          'resize-none rounded-lg border p-2 text-[13px] focus:outline-none',
          dark ? 'border-slate-600 bg-slate-800 text-white placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900',
        )}
      />
    </label>
  );

  return (
    <>
      {/* Wide screens: popover under the highlight. */}
      {position && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('exam.sat.highlight')}
          style={{ top: position.top, left: position.left }}
          className="absolute z-20 hidden w-[300px] flex-col gap-2.5 rounded-[14px] bg-[#0A1628] p-3 font-sans text-white shadow-[0_16px_30px_-12px_rgba(0,0,0,0.5)] lg:flex"
        >
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-300">{t('exam.sat.highlight')}</span>
            <button
              type="button"
              onClick={onDelete}
              title={t('exam.sat.hl_delete_help')}
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-red-300 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D1FE17]"
            >
              {t('exam.sat.hl_delete')}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('exam.nav.close')}
              className="shrink-0 rounded-lg p-1 text-slate-300 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D1FE17]"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="flex items-center gap-2.5">{swatches(false)}</div>
          {noteField(true)}
        </div>
      )}

      {/* Phones and tablets: floating toolbar above the footer. */}
      <div
        role="toolbar"
        aria-label={t('exam.sat.highlight')}
        className="fixed inset-x-4 bottom-[84px] z-30 rounded-2xl bg-[#0A1628] p-3 shadow-[0_14px_28px_-10px_rgba(0,0,0,0.5)] lg:hidden"
      >
        {noteOpen && <div className="mb-3">{noteField(true)}</div>}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center justify-between gap-1.5">{swatches(true)}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('exam.nav.close')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2 border-t border-slate-700 pt-2">
          <button
            type="button"
            onClick={() => setNoteOpen((v) => !v)}
            aria-expanded={noteOpen}
            title={t('exam.sat.hl_note_help')}
            className="h-10 min-w-0 flex-1 truncate rounded-lg px-3 text-[13px] font-semibold text-slate-200 hover:bg-slate-800"
          >
            {t('exam.sat.hl_note')}
          </button>
          <button
            type="button"
            onClick={onDelete}
            title={t('exam.sat.hl_delete_help')}
            className="h-10 min-w-0 flex-1 truncate rounded-lg px-3 text-[13px] font-semibold text-red-300 hover:bg-slate-800"
          >
            {t('exam.sat.hl_delete')}
          </button>
        </div>
      </div>
    </>
  );
}

export default HighlightPanel;
